import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Ticket, Calendar, Filter } from "lucide-react";
import { OrderDetailDialog } from "@/components/OrderDetailDialog";

function getToday() {
  return new Date().toISOString().slice(0, 10);
}
function getYesterday() {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
}

export default function OrdersPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [diningTypes, setDiningTypes] = useState([]);
  const [diningFilter, setDiningFilter] = useState("all");
  const [datePreset, setDatePreset] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    api.get("/dining-types").then((r) => setDiningTypes(r.data.filter((d) => d.is_active))).catch(() => {});
  }, []);

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (statusFilter !== "all") params.append("status", statusFilter);
        if (diningFilter !== "all") params.append("dining_type_id", diningFilter);

        let from = dateFrom;
        let to = dateTo;
        if (datePreset === "today") {
          from = getToday();
          to = getToday() + "T23:59:59";
        } else if (datePreset === "yesterday") {
          from = getYesterday();
          to = getYesterday() + "T23:59:59";
        } else if (datePreset === "custom") {
          if (from) params.append("date_from", from);
          if (to) params.append("date_to", to + "T23:59:59");
        }
        if (datePreset === "today" || datePreset === "yesterday") {
          params.append("date_from", from);
          params.append("date_to", to);
        }

        const qs = params.toString() ? `?${params.toString()}` : "";
        const res = await api.get(`/orders${qs}`);
        setOrders(res.data);
      } catch (err) {
        console.error("Failed to load orders:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [statusFilter, diningFilter, datePreset, dateFrom, dateTo]);

  const handleDatePreset = (preset) => {
    setDatePreset(preset);
    if (preset !== "custom") {
      setDateFrom("");
      setDateTo("");
    }
  };

  const totalRevenue = orders.reduce((s, o) => s + (o.total || 0), 0);

  return (
    <div className="p-8" data-testid="orders-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 tracking-tight" data-testid="orders-title">Orders</h1>
          <p className="text-sm text-slate-500 mt-1">{orders.length} orders | ${totalRevenue.toFixed(2)} total</p>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 mb-4 space-y-3" data-testid="orders-filters">
        {/* Date Presets */}
        <div className="flex items-center gap-2 flex-wrap">
          <Calendar className="h-4 w-4 text-slate-400" />
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider mr-1">Date:</span>
          {[
            { id: "all", label: "All Time" },
            { id: "today", label: "Today" },
            { id: "yesterday", label: "Yesterday" },
            { id: "custom", label: "Custom Range" },
          ].map((p) => (
            <Button
              key={p.id}
              variant={datePreset === p.id ? "default" : "outline"}
              size="sm"
              className={`text-xs h-8 ${datePreset === p.id ? "bg-[var(--brand-primary)] text-white" : ""}`}
              onClick={() => handleDatePreset(p.id)}
              data-testid={`date-preset-${p.id}`}
            >
              {p.label}
            </Button>
          ))}
          {datePreset === "custom" && (
            <div className="flex items-center gap-2 ml-2">
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-8 text-xs w-36" data-testid="date-from-input" />
              <span className="text-xs text-slate-400">to</span>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-8 text-xs w-36" data-testid="date-to-input" />
            </div>
          )}
        </div>

        {/* Status + Dining Type Filters */}
        <div className="flex items-center gap-3 flex-wrap">
          <Filter className="h-4 w-4 text-slate-400" />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-32 h-8 text-xs" data-testid="orders-status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="voided">Voided</SelectItem>
            </SelectContent>
          </Select>
          <Select value={diningFilter} onValueChange={setDiningFilter}>
            <SelectTrigger className="w-40 h-8 text-xs" data-testid="orders-dining-filter">
              <SelectValue placeholder="Order Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Order Types</SelectItem>
              {diningTypes.map((dt) => (
                <SelectItem key={dt.dining_type_id} value={dt.dining_type_id}>
                  {dt.name} ({dt.short_name})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="orders-table">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Ticket #</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Items</TableHead>
              <TableHead>Payment</TableHead>
              <TableHead>Employee</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-12">
                  <div className="animate-spin rounded-full h-6 w-6 border-2 border-[var(--brand-primary)] border-t-transparent mx-auto" />
                </TableCell>
              </TableRow>
            ) : orders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-12 text-slate-400">
                  No orders found for the selected filters.
                </TableCell>
              </TableRow>
            ) : (
              orders.map((order) => (
                <TableRow
                  key={order.order_id}
                  className="cursor-pointer hover:bg-blue-50/50 transition-colors"
                  onClick={() => setSelectedOrder(order)}
                  data-testid={`order-row-${order.order_id}`}
                >
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <Ticket className="h-3.5 w-3.5 text-[var(--brand-primary)]" />
                      <span className="font-mono font-bold text-sm text-slate-900" data-testid={`ticket-${order.order_id}`}>
                        {order.ticket_number || order.order_id?.slice(-8)}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {order.dining_type_name ? (
                      <Badge variant="outline" className="text-[10px] font-semibold">{order.dining_type_name}</Badge>
                    ) : (
                      <span className="text-xs text-slate-300">-</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-slate-500">
                    {order.created_at ? new Date(order.created_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "-"}
                  </TableCell>
                  <TableCell className="text-sm text-slate-600">{order.customer_name || "-"}</TableCell>
                  <TableCell className="text-sm">{order.items?.length || 0} items</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs capitalize">{order.payment_method}</Badge>
                  </TableCell>
                  <TableCell className="text-sm text-slate-500">{order.employee_name || "-"}</TableCell>
                  <TableCell className="text-right font-medium">${(order.total || 0).toFixed(2)}</TableCell>
                  <TableCell>
                    <Badge
                      variant={order.status === "completed" ? "default" : order.status === "voided" ? "destructive" : "secondary"}
                      className="text-xs capitalize"
                    >{order.status}</Badge>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Order Detail Dialog */}
      <OrderDetailDialog order={selectedOrder} open={!!selectedOrder} onOpenChange={(open) => { if (!open) setSelectedOrder(null); }} />
    </div>
  );
}
